// Die Provisionszeilen des Auftraggebers: filtern, eine manuelle Korrektur anlegen (§1.8 — sie ersetzt die
// Notiz-Spalte des Sheets) und eine Rückforderung abschließen (§4 Szenario 4). Der Bildschirm rechnet nichts
// nach: die Zeilen kommen fertig aus `GET /api/t/:id/provisionen`, und ob eine Zeile extern verrechnet werden
// darf, entscheidet allein der Server — `GET /provisionen` liefert kein `computation_key`.
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import {
  adjustmentSchema, writeOffSchema, type Adjustment, type WriteOff,
} from '../../../../shared/schemas/corrections'
import type { ProvisionZeile, TeamZeile, TenantEinstellungen } from '../../../api'
import { api } from '../../../api'
import { Betrag } from '../../../components/Betrag'
import { Dialog } from '../../../components/Dialog'
import { Fahne } from '../../../components/Fahne'
import { Feld } from '../../../components/Feld'
import { Knopf } from '../../../components/Knopf'
import { grundSchema, melde, pruefeKoerper } from '../../../components/korrekturen'
import { Leiste } from '../../../components/Leiste'
import { Tabelle } from '../../../components/Tabelle'
import { TabellenZustand, Zustand, fehlermeldung } from '../../../components/Zustand'
import { datum, eur } from '../../../format'
import { centsAusEingabe } from '../../../geld'
import type { SeitenProps } from '../../../screen'
import { berlinDate } from '../../../../shared/dates'

export interface ProvisionenSuche {
  empfaenger?: string
  status?: string
  monat?: string
}

/**
 * Die sieben Zustände aus `CommissionStatus` (engine/types.ts), auf Deutsch. Reiner Oberflächentext —
 * kleingeschrieben, weil dieselben Wörter im Satz unter der Tabelle und in den Dialogen wieder vorkommen
 * („extern verrechnet" ist zugleich der `settlement`-Wert, den der Abschluss einer Rückforderung schickt).
 */
export const PROVISIONSSTATUS: Readonly<Record<string, string | undefined>> = {
  open: 'offen',
  in_draft: 'im Entwurf',
  submitted: 'eingereicht',
  approved: 'freigegeben',
  paid: 'ausgezahlt',
  voided: 'storniert',
  settled_externally: 'extern verrechnet',
}

/** Der letzte Tag des Vormonats, als Vorschlag für den Stichtag — Berliner Kalender. */
function letzterTagVormonat(): string {
  const heute = berlinDate(new Date())
  const erster = `${heute.slice(0, 7)}-01`
  const d = new Date(`${erster}T12:00:00Z`)
  d.setUTCDate(0)
  return d.toISOString().slice(0, 10)
}

function statusText(status: string): string {
  return (Object.hasOwn(PROVISIONSSTATUS, status) ? PROVISIONSSTATUS[status] : undefined) ?? status
}

/** Die vier `kind`-Werte aus migrations/0001_init.sql (CHECK auf `commissions.kind`). */
const ART_TEXT: Readonly<Record<string, string | undefined>> = {
  earned: 'verdient',
  clawback: 'Rückbuchung (Rückzahlung)',
  reversal: 'Rückbuchung (Korrektur)',
  manual_adjustment: 'manuelle Korrektur',
}

function artText(kind: string): string {
  return (Object.hasOwn(ART_TEXT, kind) ? ART_TEXT[kind] : undefined) ?? kind
}

/** So viele <th> stehen im Kopf der Tabelle — die Zustandszeile darunter spannt genau darüber. */
const SPALTEN = 12

/**
 * `LIST_CAP` aus src/worker/services/reads.ts: `GET /provisionen` bricht bei 5000 Zeilen ab (`LIMIT`),
 * und der Monatsfilter wird danach in JS angewendet. Kommen genau so viele Zeilen an, ist die Liste
 * mit hoher Wahrscheinlichkeit abgeschnitten — dann sagt der Bildschirm das, statt eine unvollständige
 * Liste als vollständige auszugeben.
 */
const LIST_CAP = 5000

/** Eine Person, wie die Auswahlfelder sie anbieten — mit dem Hinweis, ob sie noch aktiv ist. */
interface Beteiligter {
  id: string
  name: string
  kuerzel: string
  aktiv: boolean
}

/**
 * Stillgelegte Auftragnehmer bleiben in der Liste. Ihre Provisionszeilen bleiben ja auch bestehen, und
 * eine Korrektur für jemanden, der gegangen ist, ist der Normalfall dieses Bildschirms (Spec §4
 * Szenario 4). Für den Filter reicht es, sie zu führen, solange sie ausgewählt sind — sonst stünde das
 * Feld bei `?empfaenger=<stillgelegt>` leer, während die Tabelle darunter gefiltert ist.
 */
function beteiligtenAuswahl(team: TeamZeile[]): Beteiligter[] {
  return team.map((t) => ({
    id: t.tenant_contractor_id, name: t.display_name, kuerzel: t.short_code, aktiv: t.active === 1,
  }))
}

function beteiligtenText(b: Beteiligter): string {
  return b.aktiv ? `${b.name} (${b.kuerzel})` : `${b.name} (${b.kuerzel}) — stillgelegt`
}

// ---------------------------------------------------------------------------------------------------
// Korrektur anlegen
// ---------------------------------------------------------------------------------------------------

interface KorrekturAnsicht {
  recipient_tc_id: string
  richtung: 'gutschrift' | 'rueckforderung'
  betrag: string
  note: string
  reason: string
}

interface KorrekturDialogProps {
  /** Das ganze Team, auch die Stillgelegten: der Server nimmt eine Korrektur für sie an. */
  beteiligte: Beteiligter[]
  laeuft: boolean
  /** Der deutsche `message` des Servers, wörtlich. */
  fehler: string | null
  onSchliessen: () => void
  onAbsenden: (koerper: Adjustment) => void
}

/**
 * Der Betrag wird ohne Vorzeichen getippt; das Vorzeichen setzt die Richtung. Wer „-250,00" in ein Feld
 * schreiben muss, um eine Rückforderung zu erfassen, schreibt irgendwann „250,00" und bucht das Gegenteil.
 */
function KorrekturAnlegenDialog(p: KorrekturDialogProps): ReactNode {
  const { register, handleSubmit, formState, setError } = useForm<KorrekturAnsicht>({
    resolver: zodResolver(
      z.object({
        recipient_tc_id: z.string().min(1, { message: 'Bitte einen Empfänger wählen.' }),
        richtung: z.enum(['gutschrift', 'rueckforderung']),
        betrag: z.string(),
        note: z.string().trim().min(1, { message: 'Bitte eine Notiz angeben; sie steht später auf der Rechnung.' }).max(500),
        reason: grundSchema,
      }),
    ),
    defaultValues: { recipient_tc_id: '', richtung: 'gutschrift', betrag: '', note: '', reason: '' },
  })
  return (
    <Dialog offen titel="Korrektur anlegen" onSchliessen={p.onSchliessen}>
      <form
        onSubmit={(ereignis) => {
          ereignis.preventDefault()
          void handleSubmit((werte) => {
            const cents = centsAusEingabe(werte.betrag)
            if (cents === null) {
              setError('betrag', { message: 'Bitte einen Betrag wie 250,00 angeben.' })
              return
            }
            const koerper: Adjustment = {
              recipient_tc_id: werte.recipient_tc_id,
              amount_cents: werte.richtung === 'rueckforderung' ? -cents : cents,
              note: werte.note,
              deal_id: null,
              reason: werte.reason,
            }
            const problem = pruefeKoerper(adjustmentSchema, koerper)
            if (problem !== null) {
              melde(
                setError,
                { recipient_tc_id: 'recipient_tc_id', amount_cents: 'betrag', note: 'note', reason: 'reason' },
                'betrag',
                problem,
              )
              return
            }
            p.onAbsenden(koerper)
          })()
        }}
      >
        <div className="erklaerung">
          <p>
            Eine Zeile, die keine Zahlung erzeugt hat: Nachzahlung, Abzug, Ausgleich. Sie bekommt eine eigene
            Provisions-Referenz und erscheint auf der nächsten Abrechnung des Empfängers.
          </p>
        </div>
        <Feld label="Empfänger" htmlFor="p-empfaenger" fehler={formState.errors.recipient_tc_id?.message}>
          <select id="p-empfaenger" {...register('recipient_tc_id')}>
            <option value="">— bitte wählen —</option>
            {p.beteiligte.map((b) => (
              <option key={b.id} value={b.id}>{beteiligtenText(b)}</option>
            ))}
          </select>
        </Feld>
        <Feld label="Richtung" htmlFor="p-richtung">
          <select id="p-richtung" {...register('richtung')}>
            <option value="gutschrift">Gutschrift an den Empfänger</option>
            <option value="rueckforderung">Rückforderung vom Empfänger</option>
          </select>
        </Feld>
        <Feld
          label="Betrag (€)"
          htmlFor="p-betrag"
          fehler={formState.errors.betrag?.message}
          hinweis="Immer ohne Vorzeichen; die Richtung steht darüber."
        >
          <input id="p-betrag" type="text" inputMode="decimal" {...register('betrag')} />
        </Feld>
        <Feld label="Notiz (steht auf der Rechnung)" htmlFor="p-notiz" fehler={formState.errors.note?.message}>
          <textarea id="p-notiz" rows={2} {...register('note')} />
        </Feld>
        <Feld label="Grund (fürs Protokoll)" htmlFor="p-grund" fehler={formState.errors.reason?.message}>
          <textarea id="p-grund" rows={2} {...register('reason')} />
        </Feld>
        {p.fehler === null ? null : <p role="alert" className="fehler">{p.fehler}</p>}
        <div className="knoepfe">
          <Knopf type="submit" variante="primaer" disabled={p.laeuft}>
            {p.laeuft ? 'Bitte warten …' : 'Korrektur anlegen'}
          </Knopf>
          <Knopf type="button" variante="still" onClick={p.onSchliessen} disabled={p.laeuft}>Abbrechen</Knopf>
        </div>
      </form>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------------------------------
// Rückforderung abschließen
// ---------------------------------------------------------------------------------------------------

interface RueckforderungProps {
  zeile: ProvisionZeile
  laeuft: boolean
  fehler: string | null
  onSchliessen: () => void
  onAbsenden: (koerper: WriteOff) => void
}

/**
 * Der Titel trägt die Referenznummer, nicht die Wörter des Absenden-Knopfes: über `aria-labelledby` ist er der
 * zugängliche Name des Fensters, und eine Suche nach „Rückforderung abschließen" fände sonst beides.
 */
function RueckforderungDialog(p: RueckforderungProps): ReactNode {
  const { register, handleSubmit, formState } = useForm<WriteOff>({
    resolver: zodResolver(writeOffSchema),
    defaultValues: { settlement: 'extern_verrechnet', reason: '' },
  })
  return (
    <Dialog offen titel={`Rückforderung #${p.zeile.ref_no} abschließen`} onSchliessen={p.onSchliessen}>
      <form
        onSubmit={(ereignis) => {
          ereignis.preventDefault()
          void handleSubmit((werte) => { p.onAbsenden(werte) })()
        }}
      >
        <div className="erklaerung">
          {p.zeile.amount_cents < 0 ? (
            <p>
              Der Server legt eine gegenläufige Zeile über <Betrag cents={-p.zeile.amount_cents} /> an und setzt beide
              auf „extern verrechnet". Der Saldo des Empfängers schließt damit, ohne dass jemand eine Rechnung über
              einen negativen Betrag stellen muss.
            </p>
          ) : (
            // Der Server prüft nur Status, Empfänger und Herkunft der Zeile, nicht das Vorzeichen — eine offene
            // Forderung des Empfängers lässt sich hierüber also genauso schließen wie eine Rückforderung. Weil
            // „extern verrechnet" ein Endzustand ist, steht hier, was diese Zeile dabei verliert.
            <>
              <p>
                Achtung: Diese Zeile ist keine Rückforderung. Sie ist eine offene Forderung des Empfängers über{' '}
                <Betrag cents={p.zeile.amount_cents} />. Der Server legt eine gegenläufige Zeile über{' '}
                <Betrag cents={-p.zeile.amount_cents} /> an und setzt beide auf „extern verrechnet" — ein Endzustand.
                Aus dieser Zeile entsteht danach weder eine Rechnung des Empfängers noch eine Auszahlung.
              </p>
              <p>
                Richtig ist das nur, wenn der Betrag außerhalb dieses Systems bereits ausgeglichen wurde oder der
                Empfänger darauf verzichtet hat. Eine irrtümlich angelegte Korrektur nehmen Sie stattdessen mit einer
                gegenläufigen Korrektur zurück — die bleibt beiden Seiten sichtbar.
              </p>
            </>
          )}
        </div>
        <Feld label="Verrechnung" htmlFor="p-settlement">
          <select id="p-settlement" {...register('settlement')}>
            <option value="extern_verrechnet">außerhalb dieses Systems verrechnet</option>
            <option value="verzichtet">darauf verzichtet</option>
          </select>
        </Feld>
        <Feld label="Grund (fürs Protokoll)" htmlFor="p-wo-grund" fehler={formState.errors.reason?.message}>
          <textarea id="p-wo-grund" rows={2} {...register('reason')} />
        </Feld>
        {p.fehler === null ? null : <p role="alert" className="fehler">{p.fehler}</p>}
        <div className="knoepfe">
          <Knopf type="submit" variante="warnung" disabled={p.laeuft}>
            {p.laeuft ? 'Bitte warten …' : 'Rückforderung abschließen'}
          </Knopf>
          <Knopf type="button" variante="still" onClick={p.onSchliessen} disabled={p.laeuft}>Abbrechen</Knopf>
        </div>
      </form>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------------------------------
// Der Bildschirm
// ---------------------------------------------------------------------------------------------------

type Ziel = { art: 'korrektur' } | { art: 'rueckforderung'; zeile: ProvisionZeile }

export function ProvisionenSeite({ tenantId, suche, setzeSuche }: SeitenProps<ProvisionenSuche>): ReactNode {
  const qc = useQueryClient()
  const [ziel, setZiel] = useState<Ziel | null>(null)
  const [meldung, setMeldung] = useState('')

  const liste = useQuery({
    queryKey: ['provisionen', tenantId, suche.empfaenger ?? '', suche.status ?? '', suche.monat ?? ''],
    queryFn: () => api.get<{ provisionen: ProvisionZeile[] }>(`/api/t/${tenantId}/provisionen`, {
      empfaenger: suche.empfaenger, status: suche.status, monat: suche.monat,
    }),
  })
  const team = useQuery({
    queryKey: ['team', tenantId],
    queryFn: () => api.get<{ team: TeamZeile[] }>(`/api/t/${tenantId}/team`),
  })

  const schreiben = useMutation<string, Error, { meldung: string; ruf: () => Promise<unknown> }>({
    mutationFn: async (auftrag) => {
      await auftrag.ruf()
      return auftrag.meldung
    },
    onSuccess: (text) => {
      setMeldung(text)
      setZiel(null)
      // Eine Korrektur ändert den Saldo des Empfängers und damit das Dashboard; eine abgeschlossene
      // Rückforderung nimmt eine Zeile aus der Prüfliste. Beides wird hier verworfen statt nachgerechnet.
      for (const schl of ['provisionen', 'dashboard', 'inbox']) {
        void qc.invalidateQueries({ queryKey: [schl, tenantId] })
      }
    },
    // Sonst stünde die Erfolgsmeldung des vorigen Vorgangs neben dem Fehler des laufenden — zwei
    // widersprüchliche Sätze zur selben Zeit, einer davon vorgelesen.
    onError: () => { setMeldung('') },
  })

  /** Öffnet einen Dialog: der vorige Versuch und seine Meldung gehören nicht in den nächsten. */
  const oeffne = (naechstes: Ziel): void => {
    schreiben.reset()
    setMeldung('')
    setZiel(naechstes)
  }

  // Ob dieser Auftraggeber Provisionen vor der Abrechnung bestätigt, steht in seinen Einstellungen.
  const einstellungen = useQuery({
    queryKey: ['einstellungen', tenantId],
    queryFn: () => api.get<{ tenant: TenantEinstellungen }>(`/api/t/${tenantId}/einstellungen`),
  })
  const bestaetigtPflicht = einstellungen.data?.tenant.provisionen_bestaetigen === 1
  const pflichtUmschalten = useMutation({
    mutationFn: (an: boolean) => api.patch<{ tenant: TenantEinstellungen }>(`/api/t/${tenantId}/einstellungen`, { provisionen_bestaetigen: an ? 1 : 0 }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['einstellungen', tenantId] }) },
  })
  const [stichtag, setStichtag] = useState(letzterTagVormonat())
  const offeneUnbestaetigt = (liste.data?.provisionen ?? []).filter((c) =>
    c.status === 'open' && c.amount_cents > 0 && c.recipient_tc_id !== null && c.bestaetigt_at === null && c.faellig_am <= stichtag)
  const bestaetigen = useMutation({
    mutationFn: () => api.post<{ bestaetigt: number; summe_cents: number }>(`/api/t/${tenantId}/provisionen/bestaetigen`, { bis: stichtag }),
    onSuccess: (antwort) => {
      setMeldung(antwort.bestaetigt === 0
        ? 'Bis zu diesem Tag war nichts mehr zu bestätigen.'
        : `${antwort.bestaetigt} ${antwort.bestaetigt === 1 ? 'Provision' : 'Provisionen'} bestätigt — jetzt abrechenbar.`)
      for (const schl of ['provisionen', 'dashboard']) void qc.invalidateQueries({ queryKey: [schl, tenantId] })
    },
  })

  const alleBeteiligten = beteiligtenAuswahl(team.data?.team ?? [])
  const filterBeteiligte = alleBeteiligten.filter((b) => b.aktiv || b.id === suche.empfaenger)

  return (
    <div className="seite">
      <h1>Provisionen</h1>
      <p role="status" aria-live="polite" className="meldung">{meldung}</p>

      {/* Am Monatsende: durchsehen, dann freigeben. Erst eine bestätigte Provision kann der Vertriebler
          abrechnen; Korrekturen brauchen keine Bestätigung. Gezählt wird aus der Liste unten — mit
          Filter also nur, was der Filter zeigt; bestätigt wird serverseitig alles bis zum Stichtag. */}
      {einstellungen.data === undefined ? null : (
        <p>
          <label>
            <input
              type="checkbox"
              checked={bestaetigtPflicht}
              disabled={pflichtUmschalten.isPending}
              onChange={(e) => { pflichtUmschalten.mutate(e.target.checked) }}
            />
            {' '}Provisionen erst nach meiner Bestätigung abrechenbar
          </label>
        </p>
      )}
      {bestaetigtPflicht ? (
        <Leiste beschriftung="Provisionen bestätigen">
          <Feld label="Bestätigen bis einschließlich" htmlFor="pb-bis">
            <input id="pb-bis" type="date" value={stichtag} onChange={(e) => { setStichtag(e.target.value) }} />
          </Feld>
          <Knopf disabled={bestaetigen.isPending || stichtag === ''} onClick={() => { setMeldung(''); bestaetigen.mutate() }}>
            Bestätigen
          </Knopf>
          <span className="leise">
            {offeneUnbestaetigt.length === 0
              ? 'Nichts offen bis zu diesem Tag.'
              : `${offeneUnbestaetigt.length} offen, zusammen ${eur(offeneUnbestaetigt.reduce((n, c) => n + c.amount_cents, 0))}`}
          </span>
        </Leiste>
      ) : null}
      {bestaetigen.isError ? <p role="alert" className="fehler">{fehlermeldung(bestaetigen.error)}</p> : null}

      {/* Die Filterleiste bleibt in jedem Zustand stehen: wer gerade gefiltert hat, muss sehen, wonach —
          sonst bezieht sich „lädt" auf nichts Sichtbares. Nur die Tabelle weicht dem Lade- und Fehlerzustand.
          `bereich` macht daraus einen benannten Bereich statt einer Feldgruppe: die Filter tragen hier den
          Satz, auf welches Datum der Monat bucht, und den findet eine Vorlesesoftware so im Verzeichnis. */}
      <Leiste beschriftung="Provisionen filtern" bereich>
        {/* „Empfängerfilter", nicht „Empfänger": der Korrektur-Dialog trägt daneben ein Feld „Empfänger", und
            zwei gleichnamige Beschriftungen auf einem Bildschirm sind nicht auseinanderzuhalten. */}
        <Feld label="Empfängerfilter" htmlFor="pf-empfaenger">
          <select
            id="pf-empfaenger"
            value={suche.empfaenger ?? ''}
            onChange={(e) => { setzeSuche({ empfaenger: e.target.value === '' ? undefined : e.target.value }) }}
          >
            <option value="">alle</option>
            {filterBeteiligte.map((b) => (
              <option key={b.id} value={b.id}>{beteiligtenText(b)}</option>
            ))}
          </select>
        </Feld>
        <Feld label="Status" htmlFor="pf-status">
          <select
            id="pf-status"
            value={suche.status ?? ''}
            onChange={(e) => { setzeSuche({ status: e.target.value === '' ? undefined : e.target.value }) }}
          >
            <option value="">alle</option>
            {Object.keys(PROVISIONSSTATUS).map((s) => <option key={s} value={s}>{statusText(s)}</option>)}
          </select>
        </Feld>
        <Feld
          label="Monat"
          htmlFor="pf-monat"
          info="Gebucht auf das Wirkdatum der Zahlungsrate; manuelle Korrekturen auf ihren Anlagetag."
        >
          <input
            id="pf-monat"
            type="month"
            value={suche.monat ?? ''}
            onChange={(e) => { setzeSuche({ monat: e.target.value === '' ? undefined : e.target.value }) }}
          />
        </Feld>
        <Knopf onClick={() => { oeffne({ art: 'korrektur' }) }}>Korrektur anlegen</Knopf>
      </Leiste>

      {liste.isPending ? <Zustand art="laedt" beschriftung="Die Provisionen werden geladen." /> : null}
      {liste.isError ? (
        <Zustand art="fehler" fehler={liste.error} onWiederholen={() => { void liste.refetch() }} />
      ) : null}
      {liste.data === undefined ? null : (
        <Tabelle
          beschriftung="Provisionen"
          kopf={
            <tr>
              <th scope="col">#</th>
              <th scope="col">Empfänger</th>
              <th scope="col">Rolle</th>
              <th scope="col">Kunde</th>
              <th scope="col">Produkt</th>
              <th scope="col" className="zahl">Rate</th>
              <th scope="col" className="zahl">Basis</th>
              <th scope="col" className="zahl">Betrag</th>
              <th scope="col">Art</th>
              <th scope="col">Status</th>
              <th scope="col">Fällig am</th>
              <th scope="col">Aktion</th>
            </tr>
          }
        >
          {liste.data.provisionen.length === 0 ? (
            <TabellenZustand
              art="leer"
              spalten={SPALTEN}
              titel="Keine Provisionen für diese Auswahl"
              hinweis="Ändern Sie den Filter, oder legen Sie unter Regeln fest, wer für dieses Produkt was bekommt. Ohne aktive Regel entsteht zu einer Zahlung keine Provision."
            />
          ) : null}
          {liste.data.provisionen.map((c) => (
            <tr key={c.id}>
              <th scope="row" className="mono">{c.ref_no}</th>
              <td>{c.empfaenger_name ?? 'ohne Empfänger'}</td>
              <td>{c.role_label}</td>
              <td>{c.customer_name ?? '—'}</td>
              <td className="mono">{c.product_code ?? '—'}</td>
              <td className="zahl mono">{c.rate_nr ?? '—'}</td>
              {/* Manuelle Korrekturen und die Gegenzeilen abgeschlossener Rückforderungen werden mit
                  `basis_cents = 0` und ohne Rate angelegt (corrections.ts, MANUAL_COMMISSION_SQL). Eine
                  gedruckte 0,00 läse sich als „ein Satz auf eine Basis von null", nicht als „es gibt keine
                  Basis" — dafür steht in dieser Tabelle überall das Gedankenstrich-Zeichen. */}
              <td className="zahl">
                {c.basis_cents === 0 && c.rate_nr === null ? '—' : <Betrag cents={c.basis_cents} />}
              </td>
              <td className="zahl"><Betrag cents={c.amount_cents} /></td>
              <td>{artText(c.kind)}</td>
              <td>
                <Fahne art={c.status === 'paid' ? 'ok' : c.status === 'open' ? 'warnung' : 'neutral'}>
                  {statusText(c.status)}
                </Fahne>
                {bestaetigtPflicht && c.status === 'open' && c.amount_cents > 0
                  ? <span className="block text-sm leise">{c.bestaetigt_at === null ? 'unbestätigt' : `bestätigt ${datum(c.bestaetigt_at.slice(0, 10))}`}</span>
                  : null}
              </td>
              <td className="mono">{datum(c.faellig_am)}</td>
              <td>
                {c.status === 'open' ? (
                  <Knopf
                    variante="still"
                    onClick={() => { oeffne({ art: 'rueckforderung', zeile: c }) }}
                  >
                    Rückforderung abschließen (#{c.ref_no})
                  </Knopf>
                ) : null}
              </td>
            </tr>
          ))}
        </Tabelle>
      )}
      {liste.data !== undefined && liste.data.provisionen.length === LIST_CAP ? (
        <p role="status" className="hinweis">
          Der Server liefert höchstens {LIST_CAP} Zeilen auf einmal, und genau so viele sind angekommen: die
          Liste ist hier zu Ende, nicht die Daten. Grenzen Sie über Empfänger, Status oder Monat ein — eine
          Summe aus dieser Ansicht wäre unvollständig.
        </p>
      ) : null}
      <p className="hinweis">
        Ob eine Zeile extern verrechnet werden darf, entscheidet der Server: Zeilen, die aus der Berechnung
        stammen, lehnt er mit einer Begründung ab, die hier wörtlich erscheint. Korrigieren Sie dann den
        Abschluss oder die Zahlung dahinter. Weil diese Liste die Herkunft einer Zeile nicht mitliefert, steht
        die Aktion an jeder offenen Zeile — auch an einer offenen Forderung des Empfängers. Was ein Abschluss
        dort bedeutet, sagt der Dialog, bevor etwas geschieht.
      </p>

      {ziel === null ? null : ziel.art === 'korrektur' ? (
        <KorrekturAnlegenDialog
          beteiligte={alleBeteiligten}
          laeuft={schreiben.isPending}
          fehler={schreiben.error?.message ?? null}
          onSchliessen={() => { schreiben.reset(); setZiel(null) }}
          onAbsenden={(koerper) => {
            schreiben.mutate({
              meldung: 'Korrektur angelegt.',
              ruf: () => api.post(`/api/t/${tenantId}/provisionen/adjustment`, koerper),
            })
          }}
        />
      ) : (
        <RueckforderungDialog
          zeile={ziel.zeile}
          laeuft={schreiben.isPending}
          fehler={schreiben.error?.message ?? null}
          onSchliessen={() => { schreiben.reset(); setZiel(null) }}
          onAbsenden={(koerper) => {
            schreiben.mutate({
              meldung: 'Rückforderung abgeschlossen.',
              ruf: () => api.post(`/api/t/${tenantId}/provisionen/${ziel.zeile.id}/write-off`, koerper),
            })
          }}
        />
      )}
    </div>
  )
}

export const Route = createFileRoute('/t/$tenantId/provisionen')({
  validateSearch: (roh: Record<string, unknown>): ProvisionenSuche => {
    const text = (schluessel: string): string | undefined => {
      const wert = roh[schluessel]
      return typeof wert === 'string' && wert !== '' ? wert : undefined
    }
    const monat = text('monat')
    const status = text('status')
    return {
      empfaenger: text('empfaenger'),
      // Ein unbekannter Status wird ebenso verworfen wie ein halber Monat: der Server nimmt jeden String
      // an und antwortet auf einen Tippfehler mit einer leeren Liste — „keine Provisionszeilen" statt
      // „diesen Filter gibt es nicht" wäre die irreführendere der beiden Antworten.
      status: status !== undefined && Object.hasOwn(PROVISIONSSTATUS, status) ? status : undefined,
      // Ein unvollständiger Monat aus der Adresszeile wird verworfen statt weitergereicht: der Server
      // beantwortet ihn mit 400, und ein Bildschirm, der wegen einer halb getippten URL rot wird, hilft nicht.
      monat: monat !== undefined && /^\d{4}-\d{2}$/.test(monat) ? monat : undefined,
    }
  },
  component: ProvisionenRoute,
})

function ProvisionenRoute(): ReactNode {
  const { tenantId } = Route.useParams()
  const suche = Route.useSearch()
  const navigate = useNavigate()
  return (
    <ProvisionenSeite
      tenantId={tenantId}
      suche={suche}
      setzeSuche={(naechste) => { void navigate({ to: '.', search: (alt: ProvisionenSuche) => ({ ...alt, ...naechste }) }) }}
      gehZu={(pfad) => { void navigate({ to: pfad }) }}
    />
  )
}
